import { FC, useMemo, useRef } from 'react';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { getFileTypeName, isImageFile } from '@nx-frontend/shared/utils/file-type';
import { ChatBotAttachedFile } from './types';

interface ChatbotAttachedFilesProps {
  files: ChatBotAttachedFile[];
  onRemoveFile?: (file: ChatBotAttachedFile) => void;
}

interface CircleProgressProps {
  progress: number;
}

const ProgressIndicator: FC<CircleProgressProps> = ({ progress }) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
      <svg className="w-10 h-10 -rotate-90" viewBox="0 0 100 100">
        <circle className="fill-transparent stroke-white stroke-opacity-30" cx="50" cy="50" r="40" strokeWidth="4" />
        <circle
          className="fill-transparent stroke-white transition-all duration-300 ease-in-out"
          cx="50"
          cy="50"
          r="40"
          strokeWidth="4"
          strokeDasharray="251.2"
          strokeDashoffset={251.2 - progress * 251.2}
        />
      </svg>
      <div className="absolute text-white text-[9px] font-normal">{Math.min(Math.round(progress * 100), 100)}%</div>
    </div>
  );
};

export const ChatbotAttachedFiles: FC<ChatbotAttachedFilesProps> = ({ files, onRemoveFile }) => {
  const fileUrls = useRef<Map<File, string>>(new Map());

  const getFileUrl = (file: File) => {
    if (!fileUrls.current.get(file)) {
      fileUrls.current.set(file, URL.createObjectURL(file));
    }
    return fileUrls.current.get(file);
  };

  const fileItems = useMemo(() => {
    return files.map((fileItem, index) => {
      const { file, uploadState, uploaded } = fileItem;
      const isImage = isImageFile(file);

      const progress = uploaded / file.size;

      return (
        <div
          key={`${file.name}-${index}`}
          className={`group flex-shrink-0 relative rounded-lg flex items-center bg-neutral-50 border border-gray-200 ${
            isImage ? 'w-16 h-16' : 'w-60 h-16 flex-row'
          }`}
        >
          {isImage ? (
            <div
              className="absolute inset-0 bg-center bg-cover bg-no-repeat"
              style={{ backgroundImage: `url(${getFileUrl(file)})` }}
              title={file.name}
            />
          ) : (
            <div className="flex flex-row w-full px-3">
              <div className="text-2xl mr-3 flex items-center justify-center bg-cyan-100 rounded-lg w-8 h-8">
                <Icon className="w-6 h-6 text-teal-500" icon={IconsMap.FILE} />
              </div>
              <div className="flex-1 overflow-hidden">
                <div className="text-xs font-medium whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                  {file.name}
                </div>
                <div className="text-[11px] text-gray-600">{getFileTypeName(file.type)}</div>
              </div>
            </div>
          )}

          {uploadState === 'uploading' && <ProgressIndicator progress={progress} />}

          {uploadState === 'error' && (
            <div className="absolute inset-0 bg-red-100 bg-opacity-80 flex items-center justify-center">
              <div className="text-red-500">
                <span role="img" aria-label="close">
                  <span>Upload failed</span>
                </span>
              </div>
            </div>
          )}

          {onRemoveFile && (
            <TippyTooltip message={'Remove file'}>
              <button
                onClick={() => onRemoveFile(fileItem)}
                className="absolute top-[-7px] right-[-7px] w-4 h-4 rounded-full text-white bg-neutral-900 bg-opacity-60 flex items-center justify-center hover:bg-opacity-80 transition-colors"
              >
                <Icon icon={IconsMap.CLOSE} className="w-3 h-3" />
              </button>
            </TippyTooltip>
          )}
        </div>
      );
    });
  }, [files, onRemoveFile]);

  if (files.length === 0) {
    return null;
  }

  return <div className="flex flex-nowrap overflow-hidden gap-4 whitespace-nowrap pt-3">{fileItems}</div>;
};
